import React, { createContext, useCallback, useContext, useEffect, useRef } from "react";

export type DismissInterceptor = (proceed: () => void) => void;
type RouteAction = "PUSH" | "POP" | "REPLACE";
type RouteLocation = unknown;
type RouteBlocker = (location: RouteLocation, action: RouteAction) => false | void;

interface DismissableContextValue {
  register: (interceptor: DismissInterceptor) => () => void;
  dismiss: (proceed: () => void) => void;
}

export const DismissableContext = createContext<DismissableContextValue | null>(null);

export const useDismissable = () => {
  const ctx = useContext(DismissableContext);
  if (!ctx) throw new Error("useDismissable must be used within a DismissableProvider");
  return ctx;
};

export interface DismissableProviderProps {
  children: React.ReactNode;
}

interface DismissGuardRouterContextValue {
  block: (blocker: RouteBlocker) => () => void;
  push: (location: RouteLocation) => void;
  replace: (location: RouteLocation) => void;
  goBack: () => void;
}

const DismissGuardRouterContext = createContext<DismissGuardRouterContextValue | null>(null);

export interface DismissGuardRouterProviderProps extends DismissGuardRouterContextValue {
  children: React.ReactNode;
}

export const DismissGuardRouterProvider = ({ children, block, push, replace, goBack }: DismissGuardRouterProviderProps) => (
  <DismissGuardRouterContext.Provider value={{ block, push, replace, goBack }}>
    {children}
  </DismissGuardRouterContext.Provider>
);

export const DismissableProvider = ({ children }: DismissableProviderProps) => {
  const interceptorRef = useRef<DismissInterceptor | null>(null);

  const register = useCallback((interceptor: DismissInterceptor) => {
    interceptorRef.current = interceptor;
    return () => {
      if (interceptorRef.current === interceptor) {
        interceptorRef.current = null;
      }
    };
  }, []);

  const dismiss = useCallback((proceed: () => void) => {
    if (interceptorRef.current) {
      interceptorRef.current(proceed);
    } else {
      proceed();
    }
  }, []);

  return (
    <DismissableContext.Provider value={{ register, dismiss }}>
      {children}
    </DismissableContext.Provider>
  );
};

export interface DismissGuardOptions {
  /** Activates the guard when `true`. */
  when: boolean;
  /**
   * Called when the user attempts to dismiss the current surface while the guard is active.
   * Receives a `proceed` callback; call it to allow dismissal.
   */
  onAttempt?: (proceed: () => void) => void;
}

const confirmAttempt = (onAttempt: DismissGuardOptions["onAttempt"], proceed: () => void) => {
  if (onAttempt) {
    onAttempt(proceed);
    return;
  }

  if (window.confirm("You have unsaved changes. Discard them?")) {
    proceed();
  }
};

export const useDismissGuard = ({ when: active, onAttempt }: DismissGuardOptions) => {
  const dismissableCtx = useContext(DismissableContext);
  const routerCtx = useContext(DismissGuardRouterContext);
  const onAttemptRef = useRef(onAttempt);
  const allowNextRouteRef = useRef(false);

  useEffect(() => { onAttemptRef.current = onAttempt; }, [onAttempt]);

  const interceptor = useCallback<DismissInterceptor>((proceed) => {
    confirmAttempt(onAttemptRef.current, proceed);
  }, []);

  useEffect(() => {
    if (!dismissableCtx) return;
    if (!active) return;
    return dismissableCtx.register(interceptor);
  }, [dismissableCtx, active, interceptor]);

  useEffect(() => {
    if (!active) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [active]);

  useEffect(() => {
    if (!active) return;
    if (!routerCtx) return;

    return routerCtx.block((location, action) => {
      if (allowNextRouteRef.current) {
        allowNextRouteRef.current = false;
        return undefined;
      }

      confirmAttempt(onAttemptRef.current, () => {
        allowNextRouteRef.current = true;

        if (action === "REPLACE") {
          routerCtx.replace(location);
          return;
        }

        if (action === "POP") {
          routerCtx.goBack();
          return;
        }

        routerCtx.push(location);
      });

      return false;
    });
  }, [active, routerCtx]);

  return { active };
};
